/*
  AMP3 redesign behaviour.

  1. Live chat links: any element with [data-a3-chatway] opens the Chatway
     widget when it has loaded, otherwise follows its normal href
     (the contact page).
  2. Saved products ("wishlist"): hearts ([data-a3-save]) store product
     handles in this browser's localStorage. The header heart
     ([data-a3-wishlist-open]) opens a panel listing them, loaded from
     /products/<handle>.js so prices and availability are always live.
*/
(function () {
  'use strict';

  /* ---------- Live chat ---------- */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-a3-chatway], a[href$="#chat"]');
    if (!link) return;
    if (window.$chatway && typeof window.$chatway.openChatwayWidget === 'function') {
      try {
        window.$chatway.openChatwayWidget();
      } catch (err) {
        return; // let the link go to the contact page instead
      }
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('a3:chat-open'));
    }
  });

  /* ---------- Chat greeting (snippets/amp3-chat-greeting.liquid) ----------
     Shown [delay] seconds after Chatway has loaded, then rests for [rest]
     minutes (in this browser, across tabs and visits) once it has been seen,
     dismissed or used to open chat; Chatway's own bubble rests for an hour.
     If nobody touches it, it fades away after [hide-after] visible seconds
     (the count pauses while the pointer or keyboard focus is on it, and
     while it is hidden behind a drawer or banner).
     It appears only while the Chatway button itself is visible (so Chatway's
     own page rules and schedules still apply). It waits while a drawer, overlay,
     menu, modal or the cookie banner is open, and goes once it is dismissed
     or chat is opened by any route.
     Chatway signals used: the chatwayLoaded / chatwayWidgetOpened document
     events and the body.chatway-is-open class, both set by Chatway's
     widget.js (checked 28 Sep 2026; the events are not in Chatway's public
     docs, so the class is watched as well), plus its .chatway--trigger-container
     element for position and visibility. */
  function initChatGreeting() {
    var card = document.getElementById('a3-chat-greeting');
    if (!card || card.dataset.a3Ready) return;
    card.dataset.a3Ready = '1';

    if (card.hasAttribute('data-editor-preview')) {
      card.hidden = false;
      card.classList.add('is-visible');
      card.querySelector('[data-a3-chatg-close]').addEventListener('click', function () { card.hidden = true; });
      return;
    }

    var KEY = 'amp3:chat-greeting:v2'; // holds the time it may next show
    var rest = (parseFloat(card.dataset.rest) || 60) * 60000;
    function seen() {
      try { return Date.now() < (parseInt(localStorage.getItem(KEY), 10) || 0); } catch (err) { return true; } // no storage: never nag
    }
    function markSeen() {
      try { localStorage.setItem(KEY, String(Date.now() + rest)); } catch (err) { /* storage blocked */ }
    }
    if (seen()) { card.remove(); return; }

    var BLOCKERS = 'side-drawer[open], compare-drawer[open], cart-drawer[open], modal-dialog[open], dialog[open], ' +
      'body.overflow-hidden, body.overlay-predictive-search, .main-nav__item--dropdown.is-open, .gallery-zoom-open, .a3-pbn.is-visible, .a3-cookies.is-visible';
    var delay = (parseFloat(card.dataset.delay) || 0) * 1000;
    var hideAfter = (parseFloat(card.dataset.hideAfter) || 0) * 1000;
    var state = 'waiting'; // waiting → due → shown (→ fading) → done
    var observer, ticking = false, giveUp;

    // Auto-hide: count down only while the card is on screen and untouched.
    var left = hideAfter, hideTimer = null, startedAt = 0, held = false;
    function runHide() {
      if (!hideAfter || hideTimer || held || state !== 'shown') return;
      startedAt = Date.now();
      hideTimer = setTimeout(fadeOut, left);
    }
    function pauseHide() {
      if (!hideTimer) return;
      clearTimeout(hideTimer);
      hideTimer = null;
      left = Math.max(1500, left - (Date.now() - startedAt));
    }
    function fadeOut() {
      hideTimer = null;
      state = 'fading';
      card.classList.remove('is-visible');
      setTimeout(finish, 300); // let the opacity transition play
    }
    function hold() { held = true; pauseHide(); }
    function release() {
      held = card.matches(':hover') || card.contains(document.activeElement);
      if (!held && card.classList.contains('is-visible')) runHide();
    }
    card.addEventListener('pointerenter', hold);
    card.addEventListener('focusin', hold);
    card.addEventListener('pointerleave', function () { setTimeout(release, 0); });
    card.addEventListener('focusout', function () { setTimeout(release, 0); });

    function chatOpen() { return document.body.classList.contains('chatway-is-open'); }
    function launcher() {
      var t = document.querySelector('.chatway--trigger-container');
      if (!t) return null;
      var r = t.getBoundingClientRect();
      if (!r.width || !r.height || getComputedStyle(t).visibility === 'hidden') return null;
      return t;
    }
    function visible(el) { return !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'; }
    function blocked() {
      return !!document.querySelector(BLOCKERS) ||
        visible(document.getElementById('shopify-pc__banner')) || visible(document.getElementById('shopify-pc__prefs'));
    }

    // Line the card up 12px above the Chatway button. offsetTop ignores the
    // sticky-bar transform, which the card shares via CSS.
    function place(t) {
      var root = document.documentElement;
      var right = Math.max(16, root.clientWidth - t.getBoundingClientRect().right);
      var bottom = root.clientHeight - t.offsetTop + 12;
      card.style.setProperty('--a3-chatg-right', right + 'px');
      card.style.setProperty('--a3-chatg-bottom', bottom + 'px');
      var shift = !document.body.classList.contains('a3-has-sticky') ? 0 : window.matchMedia('(max-width: 800px)').matches ? 70 : 78;
      return root.clientHeight - bottom - shift - card.offsetHeight >= 16; // room above?
    }

    // Dismissed, chat opened, or the card has had its turn: gone for the
    // session. Chatway missing or hidden on this page (quiet = true): just
    // stand down here, so a later page can still greet.
    function finish(quiet) {
      if (state === 'done') return;
      if (quiet !== true) markSeen();
      state = 'done';
      clearTimeout(giveUp);
      clearTimeout(hideTimer);
      if (observer) observer.disconnect();
      document.removeEventListener('chatwayLoaded', onReady);
      document.removeEventListener('chatwayWidgetOpened', finish);
      document.removeEventListener('a3:chat-open', finish);
      document.removeEventListener('visitorConsentCollected', schedule);
      window.removeEventListener('resize', schedule);
      if (card.contains(document.activeElement)) {
        var button = document.querySelector('.chatway--trigger-container .launcher-default-open-icon');
        if (button && visible(button)) button.focus(); else document.activeElement.blur();
      }
      card.remove();
    }

    function check() {
      ticking = false;
      if (state === 'done' || state === 'waiting' || state === 'fading') return;
      if (chatOpen()) { finish(); return; }
      var t = launcher();
      if (!t) {
        if (state === 'shown') finish(); else finish(true); // Chatway hid its button: go with it
        return;
      }
      var clear = !blocked();
      if (clear) {
        card.hidden = false;
        clear = place(t);
      }
      // Only touch the class when it changes: the observer below sees our own
      // class writes too.
      if (!clear) {
        pauseHide();
        card.hidden = true;
        if (card.classList.contains('is-visible')) card.classList.remove('is-visible');
        return;
      }
      if (state === 'due') { state = 'shown'; markSeen(); }
      if (!card.classList.contains('is-visible')) {
        requestAnimationFrame(function () { card.classList.add('is-visible'); runHide(); });
      }
    }
    function schedule() {
      if (!ticking) { ticking = true; requestAnimationFrame(check); }
    }

    function due(tries) {
      if (state !== 'waiting') return;
      if (seen() || chatOpen()) { finish(true); return; }
      if (!launcher()) {
        // The button can appear a moment after the load event; otherwise
        // Chatway is hidden here on purpose.
        if (tries < 5) setTimeout(function () { due(tries + 1); }, 500); else finish(true);
        return;
      }
      state = 'due';
      observer = new MutationObserver(schedule);
      observer.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'open'] });
      window.addEventListener('resize', schedule);
      document.addEventListener('visitorConsentCollected', schedule);
      check();
    }

    function onReady() {
      if (state !== 'waiting' || !window.$chatway || typeof window.$chatway.openChatwayWidget !== 'function') return;
      clearTimeout(giveUp);
      document.removeEventListener('chatwayLoaded', onReady);
      setTimeout(function () { due(0); }, delay);
    }

    card.querySelector('[data-a3-chatg-close]').addEventListener('click', finish);
    document.addEventListener('chatwayWidgetOpened', finish);
    document.addEventListener('a3:chat-open', finish);
    document.addEventListener('chatwayLoaded', onReady);
    if (document.querySelector('.chatway--container.has-loaded')) onReady();
    // Chatway starts on window load; if it hasn't arrived 15s after that
    // (blocked, slow or bot-filtered), leave the page alone.
    function startTimer() { giveUp = setTimeout(function () { if (state === 'waiting') finish(true); }, 15000); }
    if (document.readyState === 'complete') startTimer(); else window.addEventListener('load', startTimer);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initChatGreeting); else initChatGreeting();

  /* ---------- amp3 AI chooser (snippets/amp3-chat-ai-choice.liquid) ----------
     Joshua, 17 Oct 2026. When the AMP3 scheduler says the AI may be offered
     (/chat-ai/online: live mode and nobody from the team online, or this
     browser carries the team's test word), the first click on the Chatway
     button or a "chat with us" link shows a card instead of the chat window:
     "Leave a message" opens the chat as usual; "Ask amp3 AI" sends
     "Ask amp3 AI" as the visitor's first message (which opens the chat), then
     records the choice with the scheduler (/chat-ai/choose, with Chatway's
     contact id, known only once the chat exists) and tags the conversation
     amp3-ai for the team.
     Nothing is intercepted while the chat is open or once the visitor has
     chosen in this tab. ?a3ai=<word> stores the test word in this browser
     (?a3ai=off removes it); the word never shows on the page.
     Chatway's button lives in the page (not an iframe), and its handlers sit
     on the element, so capture-phase listeners can stop it (checked 17 Oct 2026). */
  function initChatAiChoice() {
    var card = document.getElementById('a3-chat-ai');
    if (!card || card.dataset.a3Ready) return;
    card.dataset.a3Ready = '1';
    var api = (card.dataset.api || '').replace(/\/+$/, '');
    var mode = card.dataset.mode || 'off';
    var WORD_KEY = 'amp3:ai:word', DECIDED_KEY = 'amp3:ai:decided';
    var word = null;
    try {
      var m = location.search.match(/[?&]a3ai=([^&#]+)/);
      if (m) {
        var v = decodeURIComponent(m[1].replace(/\+/g, ' ')).trim();
        if (v === 'off' || v === '') localStorage.removeItem(WORD_KEY); else localStorage.setItem(WORD_KEY, v);
      }
      word = localStorage.getItem(WORD_KEY);
    } catch (err) { word = null; }
    if (mode === 'team' && !word) { card.remove(); return; }
    var decided = null;
    try { decided = sessionStorage.getItem(DECIDED_KEY); } catch (err) { /* no storage: ask each page */ }
    if (decided || !api) { card.remove(); return; }

    var offer = null; // null = not known yet; the chat button works as normal until it is
    var ask = card.querySelector('[data-a3-chatai-ask]');
    var leave = card.querySelector('[data-a3-chatai-message]');
    var title = card.querySelector('.a3-chatai__title');

    function cw() { return window.$chatway || null; }
    function chatOpen() { return document.body.classList.contains('chatway-is-open'); }
    function decide(value) {
      decided = value;
      try { sessionStorage.setItem(DECIDED_KEY, value); } catch (err) { /* fine */ }
    }
    function openChat() {
      var c = cw();
      if (c && typeof c.openChatwayWidget === 'function') { try { c.openChatwayWidget(); return true; } catch (err) { /* fall through */ } }
      return false;
    }
    function hide() {
      card.classList.remove('is-visible');
      setTimeout(function () { card.hidden = true; }, 220);
    }
    function place() {
      var t = document.querySelector('.chatway--trigger-container');
      var root = document.documentElement;
      if (!t || window.matchMedia('(max-width: 800px)').matches) { card.style.removeProperty('--a3-chatai-right'); card.style.removeProperty('--a3-chatai-bottom'); return; }
      var r = t.getBoundingClientRect();
      card.style.setProperty('--a3-chatai-right', Math.max(16, root.clientWidth - r.right) + 'px');
      card.style.setProperty('--a3-chatai-bottom', (root.clientHeight - t.offsetTop + 12) + 'px');
    }
    function show() {
      place();
      card.hidden = false;
      requestAnimationFrame(function () {
        card.classList.add('is-visible');
        var first = leave || ask; if (first) first.focus({ preventScroll: true }); // the team's button is the first choice (19 Oct 2026)
      });
    }

    // Take the click on the chat button (and chat links) while the choice is still open.
    var TARGETS = '.chatway--trigger-container, [data-a3-chatway], a[href$="#chat"]';
    function intercept(e) {
      if (offer !== true || decided || chatOpen()) return;
      if (!e.target || !e.target.closest) return;
      var t = e.target.closest(TARGETS);
      if (!t || card.contains(t)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'click') show();
    }
    ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup', 'click'].forEach(function (ev) {
      document.addEventListener(ev, intercept, true);
    });

    function contactId() {
      var c = cw();
      try {
        var id = c && typeof c.getContactId === 'function' ? c.getContactId() : null;
        return Promise.resolve(id).then(function (x) { return x ? String(x) : null; }).catch(function () { return null; });
      } catch (err) { return Promise.resolve(null); }
    }
    function tag(name) {
      var c = cw();
      try { if (c && typeof c.addChatwayTags === 'function') Promise.resolve(c.addChatwayTags(name)).catch(function () {}); } catch (err) { /* optional */ }
    }
    function sendFirst(text) {
      var c = cw();
      try { if (c && typeof c.sendMessage === 'function') { Promise.resolve(c.sendMessage(text)).catch(function () {}); return true; } } catch (err) { /* optional */ }
      return false;
    }

    // Chatway only knows the contact id once the chat exists, so: send "Ask amp3 AI" first (that opens the chat and
    // starts the conversation), then wait for the id and record the choice. The scheduler switches the chat on either
    // way (in live mode the message itself is the trigger; otherwise the recorded choice is).
    function waitForContact(ms) {
      var until = Date.now() + ms;
      return new Promise(function (resolve) {
        (function tick() {
          contactId().then(function (id) {
            if (id) resolve(id); else if (Date.now() < until) setTimeout(tick, 500); else resolve(null);
          });
        })();
      });
    }
    if (ask) ask.addEventListener('click', function () {
      ask.disabled = true;
      decide('ai');
      hide();
      if (!sendFirst('Ask amp3 AI')) openChat();
      waitForContact(25000).then(function (id) {
        if (!id) return;
        tag('amp3-ai');
        if (word) tag('amp3-ai-test');
        var body = { contact: id };
        if (word) body.word = word;
        fetch(api + '/chat-ai/choose', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).catch(function () { /* the team will see the chat anyway */ });
      });
    });
    if (leave) leave.addEventListener('click', function () { decide('team'); hide(); openChat(); });
    var close = card.querySelector('[data-a3-chatai-close]');
    if (close) close.addEventListener('click', function () { decide('team'); hide(); });
    document.addEventListener('chatwayWidgetOpened', function () { if (!card.hidden) hide(); });
    window.addEventListener('resize', function () { if (!card.hidden) place(); });

    // Should the AI be offered at all? Asked once per page; the chat button behaves normally until the answer arrives.
    var url = api + '/chat-ai/online' + (word ? '?w=' + encodeURIComponent(word) : '');
    fetch(url, { credentials: 'omit' }).then(function (r) { return r.json(); }).then(function (d) {
      offer = !!(d && d.offer);
      if (!offer) { card.remove(); return; }
      if (title && d.online === true && card.dataset.headingOnline) title.textContent = card.dataset.headingOnline;
      else if (title && card.dataset.headingOffline) title.textContent = card.dataset.headingOffline;
    }).catch(function () { offer = false; card.remove(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initChatAiChoice); else initChatAiChoice();

  /* ---------- Information panels (product page "AMP3 difference") ---------- */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-a3-dialog-open]');
    if (opener) {
      var dialog = document.getElementById(opener.dataset.a3DialogOpen);
      // The Price Beat tile's dialog is the AMP3 Price Beat form itself (the Mega Better Price path went on 24 Oct 2026).
      if (dialog && dialog.showModal && !dialog.open) dialog.showModal();
      return;
    }
    var close = e.target.closest('.a3-modal__close');
    if (close) { close.closest('dialog').close(); return; }
    if (e.target.matches && e.target.matches('dialog.a3-modal')) e.target.close();
  });

  /* ---------- Long descriptions: "Read more" ---------- */
  function initCollapsibles() {
    document.querySelectorAll('[data-a3-collapsible].is-collapsible').forEach(function (box) {
      var btn = box.parentElement.querySelector('[data-a3-expand]');
      if (!btn || box.dataset.a3Ready) return;
      box.dataset.a3Ready = '1';
      if (box.scrollHeight <= 640) { box.classList.remove('is-collapsible'); return; }
      box.classList.add('is-collapsed');
      btn.hidden = false;
      btn.addEventListener('click', function () {
        var open = box.classList.toggle('is-collapsed') === false;
        btn.setAttribute('aria-expanded', open);
        btn.hidden = open;
      });
    });
  }
  // Measure after images have loaded so the height is accurate.
  if (document.readyState === 'complete') initCollapsibles(); else window.addEventListener('load', initCollapsibles);

  /* ---------- Product story: "Read the full description" (sections/amp3-product-story) ----------
     Opens the description as a centred column below both story columns. Closing it from
     further down brings the button back into view so the page doesn't jump away. */
  document.addEventListener('click', function (e) {
    var more = e.target.closest('[data-a3-story-more]');
    var less = !more && e.target.closest('[data-a3-story-less]');
    if (!more && !less) return;
    var full = more ? document.getElementById(more.getAttribute('aria-controls')) : less.closest('.a3-pstory__full');
    if (!full) return;
    var btn = more || document.querySelector('[data-a3-story-more][aria-controls="' + full.id + '"]');
    if (!btn) return;
    var open = full.hidden;
    full.hidden = !open;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (less) { btn.focus({ preventScroll: true }); btn.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  });

  /* ---------- "A deeper dive": the full description as chapters (25 Oct 2026) ----------
     Descriptions are a lead paragraph then <h3> chapters, with every picture in its own <p>.
     The intro is the "A DEEPER DIVE" eyebrow, statement and lead paragraph in a sage panel with
     the first picture filling the other half (or the top, for a wide banner); further intro
     pictures join the first chapter. Each chapter becomes a row (text one side, its
     pictures the other, alternating); text-only chapters are heading | text, one or two of them tinted.
     "Label: text" lists become tiles and spec tables sit in a panel. Sticky contents rail on
     wide screens. Without <h3>s the description stays as the centred reading column. */
  function isPicture(n) { return n.tagName === 'P' && n.querySelector('img') && !n.textContent.trim(); }
  function el(tag, cls) { var e = document.createElement(tag); if (cls) e.className = cls; return e; }
  var ddCount = 0;
  function buildDeeperDive(read) {
    var nodes = [].slice.call(read.children);
    if (!nodes.some(function (n) { return n.tagName === 'H3'; })) return;
    var intro = { text: [], media: [] }, chapters = [], cur = intro;
    nodes.forEach(function (n) {
      if (n.tagName === 'H3') { cur = { head: n, text: [], media: [] }; chapters.push(cur); return; }
      (isPicture(n) ? cur.media : cur.text).push(n);
    });
    read.classList.remove('a3-pstory__read');
    read.classList.add('a3-dd');
    if (read.closest('.a3-pstory__full')) read.closest('.a3-pstory__full').classList.add('has-dd');
    read.textContent = '';

    var first = intro.media.shift();
    if (intro.media.length) chapters[0].media = intro.media.concat(chapters[0].media);
    var top = el('div', 'a3-dd__intro' + (first ? ' has-hero' : ''));
    var introText = el('div', 'a3-dd__introtext');
    var intoHead = el('div', 'a3-dd__introhead');
    var full = read.closest('.a3-pstory__full');
    if (full) full.querySelectorAll(':scope > .a3-pstory__label, :scope > .a3-pstory__fullhead').forEach(function (n) { intoHead.appendChild(n); });
    var lead = el('div', 'a3-dd__lead');
    intro.text.forEach(function (n) { lead.appendChild(n); });
    introText.appendChild(intoHead); introText.appendChild(lead);
    if (first) {
      var heroImg = el('div', 'a3-dd__heroimg');
      heroImg.appendChild(first);
      top.appendChild(heroImg);
      // A wide banner runs across the top of the panel instead of filling one half.
      var pic = first.querySelector('img');
      var shape = function () { if (pic.naturalWidth / pic.naturalHeight > 1.6) top.classList.add('is-wide'); };
      if (pic.complete && pic.naturalWidth) shape(); else pic.addEventListener('load', shape);
    }
    top.appendChild(introText);
    read.appendChild(top);
    var reveal = [top];

    var body = el('div', 'a3-dd__chapters');
    var rail = el('nav', 'a3-dd__rail');
    rail.setAttribute('aria-label', 'In this description');
    var railList = el('ol');
    rail.appendChild(railList);
    var rows = el('div', 'a3-dd__rows');
    // Picture chapters alternate sides; text-only chapters are heading | text. The first chapter with 2+ pictures
    // and short, plain text becomes a charcoal gallery (heading | text above, pictures side by side). Every third
    // text-only chapter is tinted (grey, then sage), at most two per page and never next to another panel.
    var side = 0, dark = false, textOnly = 0, tints = 0, boxed = false, idBase = 'a3-dd' + (++ddCount) + '-';
    chapters.forEach(function (c, i) {
      var num = (i + 1 < 10 ? '0' : '') + (i + 1);
      var plain = c.text.every(function (t) { return t.tagName === 'P'; }) &&
        c.text.reduce(function (n, t) { return n + t.textContent.trim().length; }, 0) < 800;
      var cls = 'a3-dd__ch';
      var box = false;
      if (!dark && c.media.length > 1 && plain) { cls += ' has-media is-gallery is-panel is-dark'; dark = box = true; side++; }
      else if (c.media.length) cls += ' has-media' + (side++ % 2 ? ' is-flipped' : '');
      else if (textOnly++ % 3 === 0 && tints < 2 && !boxed) { cls += ' is-panel ' + (tints++ % 2 ? 'is-sage' : 'is-grey'); box = true; }
      boxed = box;
      var row = el('section', cls);
      row.id = idBase + num;
      var copy = el('div', 'a3-dd__copy');
      var head = el('div', 'a3-dd__head');
      var n = el('span', 'a3-dd__num'); n.textContent = num;
      head.appendChild(n); head.appendChild(c.head);
      var text = el('div', 'a3-dd__text');
      c.text.forEach(function (t) {
        if (t.tagName === 'UL' && t.children.length > 1 && [].every.call(t.children, function (li) { var s = li.firstElementChild; return s && s.tagName === 'STRONG' && li.textContent.trim().indexOf(s.textContent.trim()) === 0; })) {
          t.classList.add('a3-dd__tiles');
          [].forEach.call(t.querySelectorAll('li > strong:first-child'), function (s) {
            s.textContent = s.textContent.replace(/:\s*$/, '');
            var rest = s.nextSibling;
            while (rest && rest.nodeType === 3 && !rest.textContent.trim()) rest = rest.nextSibling;
            if (rest && rest.nodeType === 3) rest.textContent = rest.textContent.replace(/^\s*:?\s*(\S)/, function (m, c) { return c.toUpperCase(); });
          });
        }
        if (t.tagName === 'TABLE') { var spec = el('div', 'a3-dd__spec'); spec.appendChild(t); t = spec; }
        text.appendChild(t);
      });
      copy.appendChild(head); copy.appendChild(text);
      row.appendChild(copy);
      if (c.media.length) {
        var media = el('div', 'a3-dd__media');
        c.media.forEach(function (m) { media.appendChild(m); });
        row.appendChild(media);
      }
      rows.appendChild(row);
      var li = el('li'), a = el('a');
      a.href = '#' + row.id;
      a.innerHTML = '<span>' + num + '</span>';
      a.appendChild(document.createTextNode(c.head.textContent.trim()));
      li.appendChild(a); railList.appendChild(li);
    });
    body.appendChild(rail); body.appendChild(rows);
    read.appendChild(body);

    // Rail: the current chapter is the last one whose top has passed 40% of the screen.
    var links = rail.querySelectorAll('a'), ticking = false;
    function markCurrent() {
      ticking = false;
      var id = '';
      [].forEach.call(rows.children, function (r) { if (r.getBoundingClientRect().top < innerHeight * .4) id = '#' + r.id; });
      links.forEach(function (l) { l.classList.toggle('is-current', l.getAttribute('href') === id); });
    }
    addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(markCurrent); } }, { passive: true });

    if (!('IntersectionObserver' in window)) return;
    var seen = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); seen.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    read.classList.add('a3-dd--anim');
    reveal.concat([].slice.call(rows.children)).forEach(function (r) { seen.observe(r); });
  }
  document.querySelectorAll('[data-a3-dd]').forEach(buildDeeperDive);
  document.addEventListener('shopify:section:load', function (e) { e.target.querySelectorAll('[data-a3-dd]').forEach(buildDeeperDive); });

  /* ---------- Product stock panel ----------
     Rewrites the theme's inventory notice (product-inventory.js keeps
     data-inventory-level and the JSON quantity current on every variant
     change) into the AMP3 two-line panel. Only real quantities are shown.
     Out of stock: the second line carries the stock arrival message from
     snippets/amp3-arrival (whose own box CSS then hides). */
  var ICONS = {
    check: '<path d="m5 12 4 4L19 6"/>',
    clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>',
    mail: '<path d="M3.5 6.5h17v11h-17z"/><path d="m4 7 8 6 8-6"/>'
  };
  function titleCase(t) { return String(t).toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); }); }
  function renderStock(inv) {
    var info = inv.closest('.product-info') || document;
    var data = {};
    try { data = JSON.parse(inv.querySelector('script[type="application/json"]').textContent || '{}'); } catch (err) {}
    var level = inv.getAttribute('data-inventory-level');
    if (!level) return;
    var qty = parseInt(data.inventory_quantity, 10) || 0;
    var qtyText = qty > 10 ? '10+' : String(qty); // the page only carries up to 11 (snippets/product-inventory)
    var chosen = [].map.call(info.querySelectorAll('.js-option:checked'), function (o) { return titleCase(o.value); }).join(' / ');
    var prefix = chosen ? chosen + ' · ' : '';
    var hasNotify = !!info.querySelector('.restock-rocket-button-product');
    var arrival = info.querySelector('.a3-arrival');
    var arrivalText = arrival && (level === 'backordered' || level === 'none') ? arrival.textContent.replace(/\s+/g, ' ').trim() : '';
    var finalEl = info.querySelector('.a3-arrival.is-final'); // tagged final-stock: discontinued, these are the last (snippets/amp3-arrival)
    var finalText = finalEl ? finalEl.textContent.replace(/\s+/g, ' ').trim() : '';
    // Order on demand (tag order-on-demand; Joshua, 15 Oct 2026): in stock, with the delivery time (snippets/amp3-arrival).
    var onDemandEl = info.querySelector('.a3-arrival.is-ondemand');
    var head, sub, icon;
    if (onDemandEl && (level === 'backordered' || level === 'none')) {
      head = 'In stock'; sub = prefix + onDemandEl.textContent.replace(/\s+/g, ' ').trim(); icon = 'check';
    } else if (level === 'low' || level === 'very_low') {
      head = 'Low stock' + (qty > 0 ? ' · ' + qtyText + ' available' : ''); sub = prefix + (finalText || 'Limited availability'); icon = 'check';
    } else if (level === 'backordered') {
      head = 'Available to pre-order'; sub = prefix + (arrivalText || 'Dispatches when stock arrives'); icon = 'clock';
    } else if (level === 'none') {
      head = 'Currently out of stock'; sub = prefix + (arrivalText || (hasNotify ? 'Get an email when it returns' : 'Check back soon')); icon = 'mail';
    } else {
      head = 'In stock' + (qty > 0 ? ' · ' + qtyText + ' available' : ''); sub = prefix + (finalText || 'Ready to order'); icon = 'check';
    }
    // Nothing to buy (sold out, no pre-order): Kaching's offers would only show "Sorry, this is not currently
    // available" under every colour box, so assets/amp3-bundles.css hides them (Joshua, 12 Oct 2026).
    if (info.classList) info.classList.toggle('a3-unbuyable', level === 'none');
    inv.classList.toggle('a3-ondemand', !!(onDemandEl && (level === 'backordered' || level === 'none')));
    var box = inv.querySelector('.a3-stock');
    if (!box) {
      box = document.createElement('div');
      box.className = 'a3-stock';
      box.innerHTML = '<div class="a3-stock__text"><strong></strong><small></small></div><svg class="a3-stock__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"></svg>';
      inv.appendChild(box);
      inv.classList.add('a3-stock-ready');
    }
    if (box.querySelector('strong').textContent !== head) box.querySelector('strong').textContent = head;
    if (box.querySelector('small').textContent !== sub) box.querySelector('small').textContent = sub;
    var svg = box.querySelector('svg');
    if (svg.dataset.icon !== icon) { svg.dataset.icon = icon; svg.innerHTML = ICONS[icon]; }
  }
  function updateFinishLegend(info) {
    info.querySelectorAll('[data-a3-finish-value]').forEach(function (el) {
      var checked = el.closest('fieldset') && el.closest('fieldset').querySelector('.js-option:checked');
      if (checked) { var t = titleCase(checked.value); if (el.textContent !== t) el.textContent = t; }
    });
  }
  function initStock() {
    var info = document.querySelector('.product-main .product-info');
    if (!info) return;
    var queued = false;
    function refresh() {
      queued = false;
      info.querySelectorAll('product-inventory').forEach(renderStock);
      updateFinishLegend(info);
    }
    new MutationObserver(function () { if (!queued) { queued = true; requestAnimationFrame(refresh); } })
      .observe(info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['data-inventory-level', 'hidden'] });
    info.addEventListener('change', function () { requestAnimationFrame(refresh); });
    refresh();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initStock); else initStock();

  /* ---------- Gallery caption: mirror the theme's slide counter as "/ 01" ---------- */
  /* The "Hover to zoom" hint only shows while the current photo can zoom: the theme turns zoom off (pointer-events-none
     on the link) when the photo isn't much bigger than it's shown, and on touch screens (10 Oct 2026). */
  function initGalleryIndex() {
    var out = document.querySelector('[data-a3-gallery-index]');
    var media = document.getElementById('product-media');
    if (!out || !media) return;
    var hint = media.querySelector('.a3-gallery-hint');
    function syncHint() {
      if (!hint) return;
      var gallery = media.querySelector('media-gallery');
      var item = (gallery && gallery.currentItem) || media.querySelector('.media-viewer__item');
      var link = item && item.querySelector('.js-zoom-link');
      var zoomable = !!(link && !link.classList.contains('pointer-events-none'));
      if (hint.hidden === zoomable) hint.hidden = !zoomable;
    }
    function sync() {
      var cur = media.querySelector('.media-ctrl__current-item');
      var n = cur ? parseInt(cur.textContent, 10) : 1;
      var next = String(isNaN(n) ? 1 : n).padStart(2, '0');
      if (out.textContent !== next) out.textContent = next; // writing only on change avoids re-triggering the observer
      syncHint();
    }
    var counter = media.querySelector('.media-ctrl__current-item');
    if (counter) new MutationObserver(sync).observe(counter, { subtree: true, childList: true, characterData: true });
    sync();
    setTimeout(syncHint, 1500); // the theme decides which photos can zoom after it loads
    window.addEventListener('on:debounced-resize', function () { setTimeout(syncHint, 100); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initGalleryIndex); else initGalleryIndex();

  /* ---------- Product purchase bar ---------- */
  function initStickyBar() {
    var bar = document.querySelector('[data-a3-sticky]');
    var info = document.querySelector('.product-main .product-info');
    if (!bar || !info) return;
    var btn = bar.querySelector('[data-a3-sticky-btn]');
    var label = bar.querySelector('[data-a3-sticky-label]');

    function mainButton() { return info.querySelector('.js-product-form-main [name="add"]'); }
    function notifyButton() { return info.querySelector('.restock-rocket-button-product'); }
    function target() {
      var main = mainButton();
      if (main && !main.disabled && main.offsetParent !== null) return main;
      var notify = notifyButton();
      if (notify && notify.offsetParent !== null) return notify;
      return null;
    }
    function text(el) { return el ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }

    function update() {
      var t = target();
      btn.hidden = !t;
      if (t) label.textContent = text(t);
      bar.querySelector('[data-a3-sticky-price]').textContent = text(info.querySelector('.product-info__price .price__current .js-value'));
      var chosen = [].map.call(info.querySelectorAll('.js-option:checked'), function (o) { return o.value; }).join(' / ');
      bar.querySelector('[data-a3-sticky-variant]').textContent = chosen ? ' · ' + chosen.toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); }) : '';
      bar.querySelector('[data-a3-sticky-status]').textContent = text(info.querySelector('.a3-stock strong') || info.querySelector('.product-inventory__status'));
    }

    btn.addEventListener('click', function () { var t = target(); if (t) t.click(); });
    new MutationObserver(update).observe(info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'class', 'hidden'] });
    update();

    // The theme re-renders the add-to-cart row on variant changes, so look it
    // up afresh on each check rather than observing a node that may be replaced.
    var ticking = false;
    function position() {
      ticking = false;
      var anchor = info.querySelector('.product-info__add-to-cart') || info;
      var show = anchor.getBoundingClientRect().bottom < 0;
      if (bar.hidden === show) {
        bar.hidden = !show;
        document.body.classList.toggle('a3-has-sticky', show);
      }
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(position); }
    }, { passive: true });
    window.addEventListener('resize', position);
    position();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initStickyBar); else initStickyBar();

  /* ---------- Saved products ---------- */
  var KEY = 'amp3:saved-products';

  function read() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (err) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (err) { /* storage blocked */ }
    sync();
  }
  function toggle(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i > -1) list.splice(i, 1); else list.unshift(handle);
    write(list);
    return i === -1;
  }

  function sync() {
    var list = read();
    document.querySelectorAll('[data-a3-save]').forEach(function (btn) {
      var on = list.indexOf(btn.dataset.a3Save) > -1;
      btn.setAttribute('aria-pressed', on);
      btn.setAttribute('aria-label', on ? btn.dataset.labelRemove : btn.dataset.labelSave);
    });
    document.querySelectorAll('.a3-wishlist-count').forEach(function (el) {
      el.textContent = list.length;
      el.hidden = list.length === 0;
    });
  }

  function money(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency }).format(cents / 100);
    } catch (err) {
      return (cents / 100).toFixed(2);
    }
  }

  var dialog;
  function buildDialog() {
    dialog = document.createElement('dialog');
    dialog.className = 'a3 a3-wishlist';
    dialog.setAttribute('aria-labelledby', 'a3-wishlist-title');
    dialog.innerHTML =
      '<div class="a3-wishlist__head">' +
        '<div><p class="a3-eyebrow">YOUR SAVED LISTENS</p><h2 id="a3-wishlist-title">Saved for later.</h2></div>' +
        '<button type="button" class="a3-wishlist__close" aria-label="Close saved products">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="a3-wishlist__list" role="list"></div>' +
      '<p class="a3-wishlist__note">Saved on this device. Tap the heart on any product to add or remove it.</p>';
    document.body.appendChild(dialog);
    dialog.querySelector('.a3-wishlist__close').addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
      var remove = e.target.closest('[data-a3-remove]');
      if (remove) { toggle(remove.dataset.a3Remove); render(); }
    });
  }

  function render() {
    var box = dialog.querySelector('.a3-wishlist__list');
    var list = read();
    if (!list.length) {
      box.innerHTML = '<p class="a3-wishlist__empty">Nothing saved yet. Use the heart on any product to keep it here.</p>';
      return;
    }
    box.innerHTML = '<p class="a3-wishlist__empty">Loading…</p>';
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    Promise.all(list.map(function (handle) {
      return fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      box.innerHTML = '';
      products.forEach(function (p, n) {
        if (!p) { toggle(list[n]); return; } // product no longer exists
        var row = document.createElement('div');
        row.className = 'a3-wishlist__item';
        row.setAttribute('role', 'listitem');
        var img = p.featured_image ? p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=160' : '';
        row.innerHTML =
          '<a class="a3-wishlist__img" href="' + p.url + '">' + (img ? '<img src="' + img + '" alt="" width="80" height="80" loading="lazy">' : '') + '</a>' +
          '<div class="a3-wishlist__info">' +
            '<span class="a3-card__brand"></span>' +
            '<a class="a3-wishlist__title" href="' + p.url + '"></a>' +
            '<span class="a3-wishlist__price">' + money(p.price) + (p.available ? '' : ' · Sold out') + '</span>' +
          '</div>' +
          '<button type="button" class="a3-wishlist__remove" data-a3-remove="' + p.handle + '">Remove</button>';
        row.querySelector('.a3-card__brand').textContent = p.vendor || '';
        row.querySelector('.a3-wishlist__title').textContent = p.title;
        box.appendChild(row);
      });
      if (!box.children.length) render();
    });
  }

  document.addEventListener('click', function (e) {
    var save = e.target.closest('[data-a3-save]');
    if (save) {
      e.preventDefault();
      toggle(save.dataset.a3Save);
      return;
    }
    var open = e.target.closest('[data-a3-wishlist-open]');
    if (open) {
      e.preventDefault();
      if (!dialog) buildDialog();
      render();
      dialog.showModal();
    }
  });

  window.addEventListener('storage', function (e) { if (e.key === KEY) sync(); });
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('a3:cards-updated', sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync); else sync();
})();

/* "The finer details": show all / fewer specification rows */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-a3-specs-toggle]');
    if (!btn) return;
    var section = btn.closest('.a3-pspecs');
    var open = btn.getAttribute('aria-expanded') === 'true';
    section.toggleAttribute('data-a3-specs-collapsed', open);
    btn.setAttribute('aria-expanded', open ? 'false' : 'true');
    btn.firstElementChild.textContent = open ? btn.dataset.more : btn.dataset.less;
    if (open && section.getBoundingClientRect().top < 0) section.scrollIntoView({ behavior: 'smooth' });
  });
})();

/* ---------- Mega menu close button (snippets/amp3-mega-panel) ----------
   Uses the theme's own MainMenu.close() so state, focus and scroll locks stay consistent. */
document.addEventListener('click', function (e) {
  var btn = e.target.closest('[data-a3m-close]');
  if (!btn) return;
  var item = btn.closest('.main-nav__item--dropdown');
  var menu = btn.closest('main-menu');
  if (item && menu && typeof menu.close === 'function') {
    menu.close(item);
    var toggle = item.querySelector('.main-nav__toggle-btn');
    if (toggle) toggle.focus();
  }
});

/* ---------- Stock arrival message (snippets/amp3-arrival) ----------
   Falls back to the "awaiting" text once the expected date has passed, so a
   cached page never promises a date that has gone. Runs again on each variant swap. */
if (!customElements.get('a3-arrival')) {
  customElements.define('a3-arrival', class extends HTMLElement {
    connectedCallback() {
      var until = parseInt(this.dataset.until, 10);
      if (until && Date.now() > (until + 86400) * 1000) {
        this.textContent = this.dataset.fallback;
        this.classList.replace('is-dated', 'is-unknown');
      }
    }
  });
}

/* ---------- Delivery line (snippets/amp3-mcf-promise) ----------
   Recalculates the line with UK time. Working days are Monday to Friday minus data-holidays (gov.uk
   bank holidays + the team's closure days, comma-separated ISO dates). Before the cut-off hour
   (data-cutoff) on a working day it reads "Order in the next 2h 14m 09s …" and ticks every second (seconds: Joshua, 1 Nov 2026); after
   it, when tomorrow is a working day too (Monday to Thursday evenings, Joshua 22 Oct 2026), it counts
   down to tomorrow's cut-off; otherwise "Order now …". After the cut-off the dates are counted from the
   next working day's dispatch. Warehouse
   stock (data-source="warehouse"): next day = the working day after dispatch; standard = 3 working
   days after dispatch. Amazon-held stock: the morning check's dates (data-exp/data-std, ISO; in a basket with warehouse items too, floors: the later date wins) plus
   the calendar days until dispatch. Runs again on each variant swap.
   "Live check now" (Joshua, 28 Oct 2026; data-variant set, full form only): the link in the sentence opens a small
   postcode field in its place; the answer from AMP3 Shipping (GET /apps/delivery, Shopify app proxy: ISO dates for
   that postcode, worked out from the same cut-off) replaces the date part of the sentence. The last good postcode is
   kept in localStorage and checked by itself on later product pages ("To TR1: …"; a failed automatic check leaves the
   line as it was). Answers are reused for 10 minutes in sessionStorage. A failure, a slow answer (over 4 s) or a limit
   says "We couldn't check that just now" and never shows a date. Never names Amazon (the answer's "source" is ignored). */
var A3_POSTCODE_KEY = 'a3-postcode';
var A3_POSTCODE_RE = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/;
var A3_FAQ_REMOTE = '/pages/faqs#delivery-remote-areas';
function a3Store(kind, key, value) {
  try {
    var st = window[kind];
    if (value === undefined) return st.getItem(key);
    st.setItem(key, value);
  } catch (e) { /* private window or blocked storage: works without it */ }
  return null;
}
/* The countdown: "2h 04m 09s", "14m 09s", "09s"; noSecs (a narrow basket line): "2h 05m", "15m". */
function a3Left(secs, noSecs) {
  var p = function (n) { return (n < 10 ? '0' : '') + n; };
  if (noSecs) secs = Math.ceil(secs / 60) * 60;
  var h = Math.floor(secs / 3600), m = Math.floor(secs % 3600 / 60);
  return noSecs ? (h ? h + 'h ' + p(m) : m) + 'm' : (h ? h + 'h ' + p(m) + 'm ' : m ? m + 'm ' : '') + p(secs % 60) + 's';
}
function a3Esc(t) { var d = document.createElement('div'); d.textContent = t == null ? '' : String(t); return d.innerHTML.replace(/"/g, '&quot;'); }
var A3_AREA_RE = /^[A-Z]{1,2}[0-9][A-Z0-9]?$/;
/* Automatic checks stay well inside the server's 20 per visitor per 10 minutes: at most 10 in 10 minutes, and none for
   10 minutes after the server says "rate_limited" (sessionStorage; without it, no cap is kept). */
function a3AutoAllowed() {
  var now = Date.now(), off = parseInt(a3Store('sessionStorage', 'a3-dc-off'), 10);
  if (off && now < off) return false;
  var times = [];
  try { times = JSON.parse(a3Store('sessionStorage', 'a3-dc-auto') || '[]').filter(function (t) { return now - t < 600000; }); } catch (e) { times = []; }
  if (times.length >= 10) return false;
  times.push(now);
  a3Store('sessionStorage', 'a3-dc-auto', JSON.stringify(times));
  return true;
}
if (!customElements.get('a3-delivery')) {
  customElements.define('a3-delivery', class extends HTMLElement {
    connectedCallback() {
      this.update();
      this.timer = setInterval(this.tick.bind(this), 1000);
      this.initCheck();
      // The basket's short form refits when its box changes width (window resize, the drawer opening).
      if (this.dataset.format === 'short' && window.ResizeObserver && this.parentElement) {
        this.ro = new ResizeObserver(this.fit.bind(this, false));
        this.ro.observe(this.parentElement);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); if (this.ro) this.ro.disconnect(); }
    // Every second, only the countdown; the dates, the cut-off passing and the basket's fit once a minute and at zero.
    tick() {
      var now = Date.now(), clock = this.querySelector('[data-clock]');
      if (now >= this.refreshAt) this.update();
      else if (clock) clock.textContent = a3Left(Math.ceil((this.endsAt - now) / 1000), this.noSecs);
    }
    /* Short form: the longest date that keeps the line on one line (Joshua, 16 Oct 2026: full day on desktop, shortened
       only on phones): "Tuesday 20 October", else "Tues 20 October", else "Tues 20 Oct"; if that's still two lines
       (360px phones), the countdown drops its seconds.
       force: after the text changed; otherwise only when the box's width changed (so it can't loop). */
    fit(force) {
      var p = this.querySelector('.a3-delivery-line'), span = this.querySelector('[data-exp]');
      if (!p || !span || !this.expDate || !this.parentElement) return;
      var w = this.parentElement.clientWidth;
      if (!force && w === this.fitWidth) return;
      this.fitWidth = w;
      if (!w) return; // hidden (a closed drawer): fitted when it opens
      var lh = parseFloat(getComputedStyle(p).lineHeight) || 18;
      var forms = [[true, true], [false, true], [false, false]], clock = this.querySelector('[data-clock]');
      var left = clock ? Math.max(1, Math.ceil((this.endsAt - Date.now()) / 1000)) : 0;
      this.noSecs = false;
      if (clock) clock.textContent = a3Left(left);
      for (var i = 0; i < forms.length; i++) {
        span.textContent = this.shortDate(this.expDate, forms[i][1], forms[i][0]);
        if (p.getBoundingClientRect().height <= lh * 1.5) return;
      }
      if (clock) { this.noSecs = true; clock.textContent = a3Left(left, true); }
    }
    shortDate(d, fullMonth, fullDay) {
      var days = fullDay ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] : ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];
      var months = fullMonth ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
      return days[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + months[d.getUTCMonth()];
    }
    update() {
      var cutoff = parseInt(this.dataset.cutoff, 10) || 14;
      var holidays = (this.dataset.holidays || '').split(',');
      var iso = function (d) { return d.toISOString().slice(0, 10); };
      var working = function (d) { var w = d.getUTCDay(); return w !== 0 && w !== 6 && holidays.indexOf(iso(d)) === -1; };
      var addWorking = function (d, n) { d = new Date(d.getTime()); while (n > 0) { d.setUTCDate(d.getUTCDate() + 1); if (working(d)) n--; } return d; };
      // data-format="short": the basket's one-line form, "Order within 2h 14m, with you Tuesday 20 October." (16 Oct 2026),
      // its date set by fit() below.
      var short = this.dataset.format === 'short', self = this;
      var fmt = function (d) { return short ? self.shortDate(d, false) : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }); };
      var uk = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
      var today = new Date(Date.UTC(uk.getFullYear(), uk.getMonth(), uk.getDate(), 12));
      var before = working(today) && uk.getHours() < cutoff;
      var tomorrow = new Date(today.getTime()); tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
      var evening = !before && working(today) && working(tomorrow);
      this.before = before; this.todayIso = iso(today);
      // A live answer was worked out on the other side of the cut-off: ask again (the dates have moved on).
      if (this.answer && this.answer.before !== before && !this.checking) this.runCheck(this.answer.postcode, this.answer.auto);
      var dispatch = before ? today : addWorking(today, 1);
      var shift = Math.round((dispatch - today) / 86400000);
      var dates = {};
      if (this.dataset.source === 'warehouse') {
        dates.exp = addWorking(dispatch, 1);
        dates.std = addWorking(dispatch, 3);
      }
      // Amazon's dates: the only dates for Amazon-held stock; in a basket with warehouse items too, floors (the later wins).
      ['exp', 'std'].forEach(function (k) { if (this.dataset[k]) { var d = new Date(this.dataset[k] + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + shift); if (!dates[k] || d > dates[k]) dates[k] = d; } }, this);
      var el = this;
      Object.keys(dates).forEach(function (k) { var span = el.querySelector('[data-' + k + ']'); if (span) span.textContent = fmt(dates[k]); });
      this.expDate = dates.exp;
      this.refreshAt = Date.now() + 60000;
      var head = this.querySelector('[data-head]');
      if (!head) return;
      if (before || evening) {
        var secs = (cutoff - uk.getHours() + (evening ? 24 : 0)) * 3600 - uk.getMinutes() * 60 - uk.getSeconds();
        this.endsAt = Math.floor(Date.now() / 1000) * 1000 + secs * 1000;
        this.refreshAt = Math.min(this.refreshAt, this.endsAt);
        var left = '<strong data-clock>' + a3Left(secs, this.noSecs) + '</strong>';
        head.innerHTML = short ? '<b>Order within</b> ' + left : '<b>Order</b> in the next ' + left;
      } else {
        head.innerHTML = '<b>Order now</b>';
      }
      if (short) this.fit(true);
    }
    /* ---- "Live check now" ---- */
    initCheck() {
      var box = this.querySelector('[data-check]');
      this.rest = this.querySelector('[data-rest]');
      this.live = this.querySelector('[data-live]');
      if (!box || !this.rest || !this.dataset.variant || this.checkReady) return;
      this.checkReady = true;
      box.hidden = false;
      this.idleHtml = this.rest.innerHTML;
      this.checkSeq = 0;
      this.addEventListener('click', this.onCheckClick.bind(this));
      this.addEventListener('keydown', this.onCheckKey.bind(this));
      var saved = a3Store('localStorage', A3_POSTCODE_KEY);
      if (saved && A3_POSTCODE_RE.test(saved) && a3AutoAllowed()) this.runCheck(saved, true);
    }
    say(text) { if (this.live) this.live.textContent = text; }
    idle() {
      this.answer = null;
      this.rest.innerHTML = this.idleHtml;
      this.say('');
      this.update();
    }
    box() { return this.rest.querySelector('[data-check]'); }
    openField(value, note) {
      this.checkSeq++; // an automatic check still on its way must not replace what the customer is typing
      this.checking = false;
      if (!this.box()) this.idle();
      var box = this.box(), id = 'a3dc-' + this.dataset.variant + '-' + Math.random().toString(36).slice(2, 7);
      box.innerHTML = ' <span class="a3-delivery-check__unit"><label class="a3-sr-only" for="' + id + '">Your postcode, to check the delivery date</label>' +
        '<input id="' + id + '" class="a3-delivery-check__field" type="text" placeholder="Postcode" autocomplete="postal-code" autocapitalize="characters" spellcheck="false" maxlength="10" enterkeyhint="go" data-check-field>' +
        '<button type="button" class="a3-delivery-check__go" data-check-go>Check</button></span>' +
        (note ? ' <span class="a3-delivery-check__note" data-check-note>' + a3Esc(note) + '</span>' : '');
      var field = box.querySelector('[data-check-field]');
      field.value = value || a3Store('localStorage', A3_POSTCODE_KEY) || '';
      if (note) { field.setAttribute('aria-invalid', 'true'); field.setAttribute('aria-describedby', id + '-n'); box.querySelector('[data-check-note]').id = id + '-n'; this.say(note); }
      field.focus();
      if (!note) field.select(); // a remembered postcode is replaced by typing
    }
    submitField() {
      var field = this.querySelector('[data-check-field]');
      if (!field) return;
      var pc = field.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!A3_POSTCODE_RE.test(pc)) { this.openField(field.value, 'Please check the postcode'); return; }
      this.runCheck(pc.slice(0, -3) + ' ' + pc.slice(-3), false);
    }
    onCheckClick(e) {
      if (e.target.closest('[data-check-open]')) { e.preventDefault(); this.openField(); return; }
      if (e.target.closest('[data-check-go]')) { e.preventDefault(); this.submitField(); }
    }
    onCheckKey(e) {
      if (!e.target.closest('[data-check-field]')) return;
      // The line can sit inside the product form: Enter must not add to basket.
      if (e.key === 'Enter') { e.preventDefault(); this.submitField(); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        this.checkSeq++;
        this.idle();
        var link = this.querySelector('[data-check-open]');
        if (link) link.focus();
      }
    }
    fetchCheck(postcode) {
      var key = 'a3-dc:' + this.dataset.variant + ':' + postcode + ':' + this.todayIso + ':' + (this.before ? 'b' : 'a');
      var hit = a3Store('sessionStorage', key);
      if (hit) { try { var c = JSON.parse(hit); if (Date.now() - c.at < 600000) return Promise.resolve(c.r); } catch (e) { /* ask again */ } }
      var ctrl = window.AbortController ? new AbortController() : null;
      var t = setTimeout(function () { if (ctrl) ctrl.abort(); }, 4000), slow;
      var url = '/apps/delivery?variant=' + encodeURIComponent(this.dataset.variant) + '&postcode=' + encodeURIComponent(postcode);
      return Promise.race([
        fetch(url, { headers: { accept: 'application/json' }, credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
          .then(function (res) { return res.json(); })
          .then(function (r) { if (r && r.ok) a3Store('sessionStorage', key, JSON.stringify({ at: Date.now(), r: r })); return r; }),
        // Without AbortController (old browsers) the request can't be stopped, so the answer is given up on instead.
        new Promise(function (resolve) { slow = setTimeout(function () { resolve({ ok: false, error: 'slow' }); }, 4100); }),
      ]).catch(function () { return { ok: false, error: 'unavailable' }; }).finally(function () { clearTimeout(t); clearTimeout(slow); });
    }
    runCheck(postcode, auto) {
      var self = this, seq = ++this.checkSeq, before = this.before;
      this.checking = true;
      if (!auto) {
        var box = this.box() || (this.idle(), this.box());
        box.innerHTML = ' · Checking ' + a3Esc(postcode);
        this.say('Checking delivery to ' + postcode);
      }
      this.fetchCheck(postcode).then(function (r) {
        if (seq !== self.checkSeq) return;
        self.checking = false;
        if (r && r.error === 'rate_limited') a3Store('sessionStorage', 'a3-dc-off', String(Date.now() + 600000));
        if (r && r.ok && A3_AREA_RE.test(r.area || '') && ((r.nextDay && self.dateText(r.nextDay)) || (r.standard && self.dateText(r.standard)))) {
          a3Store('localStorage', A3_POSTCODE_KEY, postcode);
          self.showAnswer(r, { postcode: postcode, auto: auto, before: before });
        } else if (auto) {
          // A failed automatic check leaves the line as it was, link included.
          if (self.answer) self.idle();
        } else if (r && r.error === 'invalid_postcode') {
          self.openField(postcode, 'Please check the postcode');
        } else {
          self.showFailure();
        }
      });
    }
    dateText(isoDay) {
      var d = new Date(isoDay + 'T12:00:00Z');
      if (isNaN(d)) return '';
      return window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? this.shortDate(d, false, false) : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
    }
    showAnswer(r, meta) {
      var to = a3Esc(r.area), nd = r.nextDay && this.dateText(r.nextDay), sd = r.standard && this.dateText(r.standard);
      var speed = nd ? 'next day' : 'standard', when = nd || sd, html, said;
      html = meta.auto ? ' for ' + speed + ' delivery. To ' + to + ': with you by <b>' + when + '</b>.' : ' for ' + speed + ' delivery to ' + to + ', with you by <b>' + when + '</b>.';
      said = (nd ? 'Next day' : 'Standard') + ' delivery to ' + r.area + ': with you by ' + when + '.';
      if (nd && sd && !this.hasAttribute('data-no-standard')) { html += ' <span class="a3-delivery-line__std">Standard delivery: ' + sd + '.</span>'; said += ' Standard delivery: ' + sd + '.'; }
      if (!nd) html += ' <a class="a3-delivery-line__link" href="' + A3_FAQ_REMOTE + '">Why not next day?</a>';
      html += ' <button type="button" class="a3-delivery-line__link" data-check-open aria-label="Change postcode (' + to + ')">Change</button>';
      this.answer = meta;
      this.rest.innerHTML = html;
      if (!meta.auto) { this.say(said); this.rest.querySelector('[data-check-open]').focus({ preventScroll: true }); }
    }
    showFailure() {
      this.answer = null;
      var box = this.box() || (this.idle(), this.box());
      box.innerHTML = ' · We couldn\'t check that just now: <a class="a3-delivery-line__link" href="' + A3_FAQ_REMOTE + '">some areas take longer</a>. <button type="button" class="a3-delivery-line__link" data-check-open>Try again</button>';
      this.say('We couldn\'t check that just now.');
      box.querySelector('[data-check-open]').focus({ preventScroll: true });
    }
  });
}

/* ---------- Product page section nav (sections/amp3-product-story) ----------
   Drops links whose section isn't on the page, opens every spec row from
   "The finer details", and shows/hides the "In the box" and "Downloads"
   strips in place. A #a3-inbox / #a3-downloads link opens its strip on load. */
(function () {
  var nav = document.querySelector('.a3-pnav');
  if (!nav) return;
  nav.querySelectorAll('a[href^="#"]').forEach(function (a) {
    if (!document.getElementById(a.getAttribute('href').slice(1))) a.remove();
  });
  function setOpen(section, open) {
    section.hidden = !open;
    var link = nav.querySelector('[aria-controls="' + section.id + '"]');
    if (link) link.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-a3-reveal]');
    if (link) {
      e.preventDefault();
      var section = document.getElementById(link.getAttribute('aria-controls'));
      var open = section.hidden;
      setOpen(section, open);
      if (open) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    var close = e.target.closest('[data-a3-reveal-close]');
    if (close) {
      setOpen(close.closest('.a3-preveal'), false);
      nav.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (e.target.closest('[data-a3-open-specs]')) {
      var specs = document.getElementById('a3-specs');
      var toggle = specs && specs.hasAttribute('data-a3-specs-collapsed') && specs.querySelector('[data-a3-specs-toggle]');
      if (toggle) toggle.click();
    }
  });
  var direct = location.hash && nav.querySelector('[data-a3-reveal][href="' + location.hash + '"]');
  if (direct) direct.click();
})();
